import { QueryClient } from "@tanstack/react-query";
import type { ApiError } from "./types/ApiError";

export const API_BASE = "/api/v1";

/** A failed API call. `code` is the stable server error code (or `network`). */
export class ApiErrorException extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiErrorException";
    this.code = code;
    this.status = status;
  }
}

export const SESSION_KEY = ["session"] as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failures, error) =>
        !(error instanceof ApiErrorException && error.status >= 400 && error.status < 500) && failures < 2,
    },
  },
});

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["X-Mixla-Csrf"] = "1";
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiErrorException("network", "Network error", 0);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const err = (data as ApiError | undefined)?.error;
    if (res.status === 401 && !path.startsWith("/auth/")) {
      void queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    }
    throw new ApiErrorException(err?.code ?? "internal", err?.message ?? res.statusText, res.status);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const get = <T>(path: string) => api<T>("GET", path);
export const put = <T>(path: string, body: unknown) => api<T>("PUT", path, body);
export const post = <T>(path: string, body?: unknown) => api<T>("POST", path, body ?? {});
export const patch = <T>(path: string, body: unknown) => api<T>("PATCH", path, body);
export const del = <T = void>(path: string) => api<T>("DELETE", path);

/** Uploads a file straight to S3 with a presigned PUT URL. */
export async function uploadToPresignedUrl(url: string, file: File, contentType = file.type): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { method: "PUT", body: file, headers: { "Content-Type": contentType } });
  } catch {
    throw new ApiErrorException("storage_unavailable", "Upload failed", 0);
  }
  if (!res.ok) throw new ApiErrorException("storage_unavailable", `Upload failed (${res.status})`, res.status);
}
